import React from "react";
import { extent } from "d3-array";
import { set } from "d3-collection";
import flattenDeep from "lodash.flattendeep";

import Chart from "../Chart";

import {
  last,
  isObject,
  getClosestItem,
  zipper,
  isDefined,
  isNotDefined,
  functor,
  mapObject,
  find,
  shallowEqual,
} from "./index";

export function getChartOrigin(origin, contextWidth, contextHeight) {
  const originCoordinates =
    typeof origin === "function" ? origin(contextWidth, contextHeight) : origin;
  return originCoordinates;
}

export function getDimensions({ width, height }, chartProps) {
  const chartHeight = chartProps.height || height;

  return {
    availableHeight: height,
    width,
    height: chartHeight,
  };
}

function values(func) {
  return (d) => {
    const obj = func(d);
    if (isObject(obj)) {
      return mapObject(obj);
    }
    return obj;
  };
}

function isArraySize2AndNumber(yExtentsProp) {
  if (Array.isArray(yExtentsProp) && yExtentsProp.length === 2) {
    const [a, b] = yExtentsProp;
    return typeof a == "number" && typeof b == "number";
  }
  return false;
}

export function getNewChartConfig(
  innerDimension,
  children,
  existingChartConfig = [],
) {
  return React.Children.map(children, (each) => {
    if (each && each.type.toString() === Chart.toString()) {
      const chartProps = {
        ...Chart.defaultProps,
        ...each.props,
      };
      const {
        id,
        origin,
        padding,
        yExtents: yExtentsProp,
        yScale: yScaleProp,
        flipYScale,
        yExtentsCalculator,
      } = chartProps;

      const yScale = yScaleProp.copy();
      const { width, height, availableHeight } = getDimensions(
        innerDimension,
        chartProps,
      );

      const { yPan } = chartProps;
      let { yPanEnabled } = chartProps;
      // var { yMousePointerRectWidth: rectWidth, yMousePointerRectHeight: rectHeight, yMousePointerArrowWidth: arrowWidth } = each.props;
      // var mouseCoordinates = { at, yDisplayFormat, rectHeight, rectWidth, arrowWidth };
      const yExtents = isDefined(yExtentsProp)
        ? (Array.isArray(yExtentsProp) ? yExtentsProp : [yExtentsProp]).map(
            functor,
          )
        : undefined;

      const prevChartConfig = find(existingChartConfig, (d) => d.id === id);

      // Preserve yPanEnabled from previous config if it was enabled (user has panned vertically)
      if (isDefined(prevChartConfig) && prevChartConfig.yPanEnabled) {
        yPanEnabled = true;
      }

      if (isArraySize2AndNumber(yExtentsProp)) {
        if (
          isDefined(prevChartConfig) &&
          prevChartConfig.yPan &&
          prevChartConfig.yPanEnabled &&
          yPan &&
          yPanEnabled &&
          shallowEqual(prevChartConfig.originalYExtentsProp, yExtentsProp)
        ) {
          // console.log(prevChartConfig.originalYExtentsProp, yExtentsProp)
          // console.log(prevChartConfig.yScale.domain())
          yScale.domain(prevChartConfig.yScale.domain());
        } else {
          const [a, b] = yExtentsProp;
          yScale.domain([a, b]);
        }
      } else if (isDefined(prevChartConfig) && prevChartConfig.yPanEnabled) {
        if (isArraySize2AndNumber(prevChartConfig.originalYExtentsProp)) {
          // do nothing
        } else {
          // Y-pan persistence disabled for Volume indicators to allow dynamic recalculation
          // This prevents volume from getting stuck with old Y-axis domains
          const isVolumeChart = prevChartConfig.yAxisId === "Volume";
          if (!isVolumeChart) {
            yScale.domain(prevChartConfig.yScale.domain());
          }
        }
      }

      return {
        id,
        origin: functor(origin)(width, availableHeight),
        padding,
        originalYExtentsProp: yExtentsProp,
        yExtents,
        yExtentsCalculator,
        flipYScale,
        // yScale: setRange(yScale.copy(), height, padding, flipYScale),
        yScale: yScale,
        yPan,
        yPanEnabled,
        // mouseCoordinates,
        width,
        height,
      };
    }
    return undefined;
  }).filter((each) => isDefined(each));
}
export function getCurrentCharts(chartConfig, mouseXY) {
  const currentCharts = chartConfig
    .filter((eachConfig) => {
      const top = eachConfig.origin[1];
      const bottom = top + eachConfig.height;
      return mouseXY[1] > top && mouseXY[1] < bottom;
    })
    .map((config) => config.id);

  return currentCharts;
}

function setRange(scale, height, padding, flipYScale) {
  if (scale.rangeRoundPoints || isNotDefined(scale.invert)) {
    if (isNaN(padding))
      throw new Error("padding has to be a number for ordinal scale");
    if (scale.rangeRoundPoints)
      scale.rangeRoundPoints(flipYScale ? [0, height] : [height, 0], padding);
    if (scale.rangeRound)
      scale.range(flipYScale ? [0, height] : [height, 0]).padding(padding);
  } else {
    const { top, bottom } = isNaN(padding)
      ? padding
      : { top: padding, bottom: padding };

    scale.range(flipYScale ? [top, height - bottom] : [height - bottom, top]);
  }
  return scale;
}

function yDomainFromYExtents(yExtents, yScale, plotData) {
  const yValues = yExtents.map((eachExtent) =>
    plotData.map(values(eachExtent)),
  );

  const allYValues = flattenDeep(yValues);

  const realYDomain = yScale.invert
    ? extent(allYValues)
    : set(allYValues).values();

  return realYDomain;
}

// Track previous yExtents for main chart to avoid duplicate logs
let lastMainChartYExtents = null;

export function getChartConfigWithUpdatedYScales(
  chartConfig,
  { plotData, xAccessor, displayXAccessor, fullData },
  xDomain,
  dy,
  chartsToPan,
) {
  const yDomains = chartConfig.map(
    ({ yExtentsCalculator, yExtents, yScale, id, type }) => {
      const realYDomain = isDefined(yExtentsCalculator)
        ? yExtentsCalculator({
            plotData,
            xDomain,
            xAccessor,
            displayXAccessor,
            fullData,
          })
        : yDomainFromYExtents(yExtents, yScale, plotData);

      // Log yExtents calculation for main chart (OHLC/candlestick) only when values change
      if (id === 1 || type === "main") {
        const currentMin = realYDomain[0];
        const currentMax = realYDomain[1];

        if (
          !lastMainChartYExtents ||
          lastMainChartYExtents.min !== currentMin ||
          lastMainChartYExtents.max !== currentMax
        ) {
          lastMainChartYExtents = { min: currentMin, max: currentMax };
        }
      }

      // console.log("yScale.domain() ->", yScale.domain())

      const yDomainDY = isDefined(dy)
        ? yScale
            .range()
            .map((each) => each - dy)
            .map(yScale.invert)
        : yScale.domain();
      return {
        realYDomain,
        yDomainDY,
        prevYDomain: yScale.domain(),
      };
    },
  );

  const combine = zipper().combine(
    (config, { realYDomain, yDomainDY, prevYDomain }) => {
      const {
        id,
        padding,
        height,
        yScale,
        yPan,
        flipYScale,
        yPanEnabled = false,
      } = config;

      const another = isDefined(chartsToPan)
        ? chartsToPan.indexOf(id) > -1
        : true;

      // Enable vertical pan automatically when there's vertical movement (dy != 0)
      // This allows dragging to move the chart both horizontally and vertically
      const hasVerticalMovement = isDefined(dy) && dy !== 0;
      const shouldUseVerticalPan = yPan && (yPanEnabled || hasVerticalMovement);

      // Determine the domain to use:
      // - If vertical pan is active and there's movement (dy), use the displaced domain
      // - If vertical pan is enabled but no movement (data update), preserve current domain
      // - Otherwise, use the auto-calculated domain
      let domain;
      if (shouldUseVerticalPan) {
        if (hasVerticalMovement) {
          // During active pan, apply displacement
          domain = another ? yDomainDY : prevYDomain;
        } else {
          // Data update while yPanEnabled - preserve current domain
          domain = prevYDomain;
        }
      } else {
        domain = realYDomain;
      }

      // console.log(id, yPan, yPanEnabled, another);
      // console.log(domain, realYDomain, prevYDomain);
      const newYScale = setRange(
        yScale.copy().domain(domain),
        height,
        padding,
        flipYScale,
      );
      return {
        ...config,
        yScale: newYScale,
        realYDomain: realYDomain,
        // Enable yPanEnabled if vertical movement detected during pan
        yPanEnabled: shouldUseVerticalPan || yPanEnabled,
      };
      // return { ...config, yScale: yScale.copy().domain(domain).range([height - padding, padding]) };
    },
  );

  const updatedChartConfig = combine(chartConfig, yDomains);
  // console.error(yDomains, dy, chartsToPan, updatedChartConfig.map(d => d.yScale.domain()));
  // console.log(updatedChartConfig.map(d => ({ id: d.id, domain: d.yScale.domain() })))

  return updatedChartConfig;
}

export function getCurrentItem(xScale, xAccessor, mouseXY, plotData) {
  let xValue, item;
  if (xScale.invert) {
    xValue = xScale.invert(mouseXY[0]);
    item = getClosestItem(plotData, xValue, xAccessor);
  } else {
    const d = xScale
      .range()
      .map((d, idx) => ({ x: Math.abs(d - mouseXY[0]), idx }))
      .reduce((a, b) => (a.x < b.x ? a : b));
    item = isDefined(d) ? plotData[d.idx] : plotData[0];
    // console.log(d, item);
  }
  return item;
}

export function getXValue(xScale, xAccessor, mouseXY, plotData) {
  let xValue, item;
  if (xScale.invert) {
    xValue = xScale.invert(mouseXY[0]);
    if (xValue > xAccessor(last(plotData)) && xScale.value) {
      return Math.round(xValue);
    } else {
      item = getClosestItem(plotData, xValue, xAccessor);
    }
  } else {
    const d = xScale
      .range()
      .map((d, idx) => ({ x: Math.abs(d - mouseXY[0]), idx }))
      .reduce((a, b) => (a.x < b.x ? a : b));
    item = isDefined(d) ? plotData[d.idx] : plotData[0];
    // console.log(d, item);
  }
  return xAccessor(item);
}

/**
 * Find a bar in chart data by its timestamp.
 * Used for restoring drawing positions after chart data changes (Issue #104).
 *
 * @param {Array} chartData - The chart data array with date information
 * @param {number} timestamp - The timestamp to search for (milliseconds since epoch)
 * @param {number} tolerance - Maximum time difference allowed (default: periodicity-based auto-tolerance)
 * @returns {Object|null} - The closest bar within tolerance, or null if not found
 */
export function findBarByTimestamp(chartData, timestamp, tolerance = null) {
  if (!chartData?.length || !timestamp) return null;

  // Date accessor that handles both bar.date and bar.idx.date
  const dateAccessor = (d) => {
    const date = d?.date || d?.idx?.date;
    return date instanceof Date
      ? date.getTime()
      : typeof date === "number"
        ? date
        : 0;
  };

  // Use binary search via getClosestItem for O(log n) performance
  const closest = getClosestItem(chartData, timestamp, dateAccessor);

  if (!closest) return null;

  // If no tolerance specified, auto-calculate based on data periodicity
  // (difference between consecutive bars)
  let effectiveTolerance = tolerance;
  if (effectiveTolerance === null && chartData.length >= 2) {
    // Calculate average bar interval from first few bars
    const firstDate = dateAccessor(chartData[0]);
    const secondDate = dateAccessor(chartData[1]);
    const barInterval = Math.abs(secondDate - firstDate);
    // Allow 1.5x the bar interval as tolerance
    effectiveTolerance = barInterval * 1.5;
  }

  // Default fallback tolerance: 1 day (for daily charts or when calculation fails)
  if (!effectiveTolerance || effectiveTolerance <= 0) {
    effectiveTolerance = 24 * 60 * 60 * 1000; // 1 day in milliseconds
  }

  // Verify the closest bar is within tolerance
  const closestDate = dateAccessor(closest);
  if (Math.abs(closestDate - timestamp) <= effectiveTolerance) {
    return closest;
  }

  return null;
}
